import React, { useEffect, useRef, useState } from "react";
import { api, AIConfigMulti } from "../api";

const PROVIDER_OPTIONS = [
  { value: "gemini",     label: "Google Gemini",  icon: "auto_awesome",   defaultUrl: "" },
  { value: "openai",     label: "OpenAI",         icon: "smart_toy",      defaultUrl: "https://api.openai.com/v1" },
  { value: "anthropic",  label: "Anthropic",      icon: "psychology",     defaultUrl: "https://api.anthropic.com/v1" },
  { value: "nvidia_nim", label: "NVIDIA NIM",     icon: "memory",         defaultUrl: "https://integrate.api.nvidia.com/v1" },
  { value: "ollama",     label: "Ollama Cloud",   icon: "cloud",          defaultUrl: "https://ollama.com" },
  { value: "zen",        label: "OpenCode Zen",   icon: "bolt",           defaultUrl: "https://opencode.ai/zen/v1" },
];

// Zen chạy không cần API key.
function isKeylessProvider(name: string): boolean {
  return name === "zen";
}

// Clear image/embed only. Do NOT match diffusiongemma / gemma-*-it (chat on NIM).
const NON_CHAT_MODEL_RE = /stable[-_]?diffusion|sdxl|\bflux\b|embedding|rerank/i;
const NON_CHAT_LABEL_HINT = /ảnh\/embed|không dùng chat/i;

function isNonChatModel(idOrName: string): boolean {
  const s = idOrName.toLowerCase();
  // User-proven chat on NIM; never label "không dùng chat"
  if (s.includes("diffusiongemma") || /gemma[\w./-]*-it\b/.test(s)) return false;
  return NON_CHAT_MODEL_RE.test(s);
}

/** Select option text: keep backend name (may include · ảnh/embed); append client hint if missing. */
function formatModelOptionLabel(m: { id: string; name: string; size?: number }): string {
  let label = m.name;
  if (isNonChatModel(`${m.id} ${m.name}`) && !NON_CHAT_LABEL_HINT.test(label)) {
    label = `${label} (không dùng chat)`;
  }
  if (m.size) label += ` (${(m.size / 1e9).toFixed(1)}GB)`;
  return label;
}

function providerMeta(name: string) {
  return PROVIDER_OPTIONS.find(p => p.value === name) || { value: name, label: name, icon: "extension", defaultUrl: "" };
}

export default function AIConfigPage() {
  const [cfg, setCfg] = useState<AIConfigMulti | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [activeProvider, setActiveProvider] = useState("");
  const [activeModel, setActiveModel] = useState("");
  const [scannedModels, setScannedModels] = useState<{ id: string; name: string; size?: number }[]>([]);
  const [scanning, setScanning] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [modalProvider, setModalProvider] = useState("gemini");
  const [modalApiKey, setModalApiKey] = useState("");
  const [modalExtraKeys, setModalExtraKeys] = useState<string[]>([]);
  const [extrasLoaded, setExtrasLoaded] = useState(false);
  const [modalBaseUrl, setModalBaseUrl] = useState("");
  const [modalSaving, setModalSaving] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [revealing, setRevealing] = useState(false);

  const [translateInput, setTranslateInput] = useState("");
  const [translateResult, setTranslateResult] = useState("");
  const [translating, setTranslating] = useState(false);

  const [priceForm, setPriceForm] = useState({ item_name: "", cost: "", category: "" });
  const [priceResult, setPriceResult] = useState<{ suggested_price?: number; reason?: string; raw?: string } | null>(null);
  const [pricing, setPricing] = useState(false);
  const nonChatWarnedRef = useRef(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.ai.configMulti();
      setCfg(r);
      setActiveProvider(r.active || "");
      setActiveModel(r.model || "");
      setScannedModels([]);
    } catch (e: any) {
      alert("Lỗi tải config: " + (e.message || "unknown"));
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const saveActive = async () => {
    if (isNonChatModel(activeModel) && !nonChatWarnedRef.current) {
      nonChatWarnedRef.current = true;
      alert(
        "Model này có vẻ là ảnh/embed — trợ lý chat có thể lỗi Empty content. Nên chọn llama/qwen/mistral/gemma chat.",
      );
    }
    setSaving(true);
    try {
      await api.ai.setActive(activeProvider, activeModel);
      await load();
    } catch (e: any) {
      alert("Lỗi: " + (e.message || "unknown"));
    }
    setSaving(false);
  };

  const scanModelsForActive = async () => {
    if (!activeProvider) { alert("Chọn provider trước"); return; }
    setScanning(true);
    setScannedModels([]);
    try {
      const r = await api.ai.scanMulti(activeProvider);
      setScannedModels(r.models || []);
      if (!r.models?.length) alert("Không tìm thấy model nào");
    } catch (e: any) {
      alert("Lỗi scan: " + (e.message || "unknown"));
    }
    setScanning(false);
  };

  const openAdd = () => {
    setModalMode("add");
    const firstAvailable = PROVIDER_OPTIONS.find(p => !cfg?.providers[p.value])?.value || "gemini";
    setModalProvider(firstAvailable);
    setModalApiKey("");
    setModalExtraKeys([]);
    setExtrasLoaded(true); // add: extras state known (empty)
    setModalBaseUrl(providerMeta(firstAvailable).defaultUrl);
    setShowKey(false);
    setShowModal(true);
  };

  const openEdit = async (name: string) => {
    setModalMode("edit");
    setModalProvider(name);
    setModalApiKey("");
    setModalExtraKeys([]);
    setExtrasLoaded(false); // chưa reveal → save không được wipe extras
    setModalBaseUrl(cfg?.providers[name]?.base_url || providerMeta(name).defaultUrl);
    setShowKey(false);
    setShowModal(true);
    // Tự động reveal key đã lưu (kể cả key phụ) để user thấy + sửa được ngay.
    setRevealing(true);
    try {
      const r = await api.ai.revealKey(name);
      const primary = (r.api_key || "").trim();
      setModalApiKey(r.api_key || "");
      setModalExtraKeys(
        (r.api_keys || []).filter(k => k.trim() && k.trim() !== primary),
      );
      setExtrasLoaded(true);
    } catch (e: any) {
      console.warn("reveal key on openEdit failed:", e?.message);
      // extrasLoaded stays false → save omits api_keys
    } finally {
      setRevealing(false);
    }
  };

  const countProviderKeys = (p: { api_key_status: "set" | ""; api_keys_status?: Array<"set" | ""> }) => {
    const main = p.api_key_status === "set" ? 1 : 0;
    const extra = (p.api_keys_status || []).filter(s => s === "set").length;
    return main + extra;
  };

  // Con mắt: lần đầu bấm xem → gọi API lấy key thật đã lưu (key bị che, không trả sẵn về client).
  const toggleShowKey = async () => {
    if (showKey) { setShowKey(false); return; }
    const needReveal = modalMode === "edit" && !modalApiKey && modalExtraKeys.every(k => !k.trim());
    if (needReveal) {
      setRevealing(true);
      try {
        const r = await api.ai.revealKey(modalProvider);
        const primary = (r.api_key || "").trim();
        setModalApiKey(r.api_key || "");
        setModalExtraKeys(
          (r.api_keys || []).filter(k => k.trim() && k.trim() !== primary),
        );
        setExtrasLoaded(true);
      } catch (e: any) {
        alert("Không lấy được key: " + (e.message || "unknown"));
        setRevealing(false);
        return;
      }
      setRevealing(false);
    }
    setShowKey(true);
  };

  const onProviderChange = (name: string) => {
    setModalProvider(name);
    setModalBaseUrl(providerMeta(name).defaultUrl);
    // Đổi provider → key gõ cho provider cũ không còn ý nghĩa; keyless thì xoá luôn, không lưu key thừa.
    if (isKeylessProvider(name)) { setModalApiKey(""); setModalExtraKeys([]); }
  };

  const saveProvider = async () => {
    const extraTrimmed = modalExtraKeys.map(k => k.trim()).filter(Boolean);
    if (modalMode === "add" && !isKeylessProvider(modalProvider) && !modalApiKey.trim() && extraTrimmed.length === 0) {
      alert("Cần ít nhất một API key khi thêm mới");
      return;
    }
    setModalSaving(true);
    try {
      const data: { api_key?: string; api_keys?: string[]; base_url?: string } = { base_url: modalBaseUrl };
      if (modalApiKey.trim()) data.api_key = modalApiKey.trim();
      // Chỉ gửi api_keys khi đã load/reveal extras — tránh wipe key phụ nếu reveal fail.
      if (extrasLoaded) data.api_keys = extraTrimmed;
      await api.ai.upsertProvider(modalProvider, data);
      setShowModal(false);
      await load();
    } catch (e: any) {
      alert("Lỗi lưu: " + (e.message || "unknown"));
    }
    setModalSaving(false);
  };

  const removeProvider = async (name: string) => {
    if (!confirm(`Xoá provider ${providerMeta(name).label}?`)) return;
    try {
      await api.ai.removeProvider(name);
      await load();
    } catch (e: any) {
      alert("Lỗi xoá: " + (e.message || "unknown"));
    }
  };

  const doTranslate = async () => {
    if (!translateInput.trim()) return;
    setTranslating(true);
    setTranslateResult("");
    try {
      const r = await api.ai.translate(translateInput);
      setTranslateResult(r.translated);
    } catch (e: any) {
      setTranslateResult("Lỗi: " + (e.message || "unknown"));
    }
    setTranslating(false);
  };

  const doSuggestPrice = async () => {
    if (!priceForm.item_name) return;
    setPricing(true);
    setPriceResult(null);
    try {
      const payload = {
        item_name: priceForm.item_name,
        cost: priceForm.cost ? Number(priceForm.cost) : undefined,
        category: priceForm.category || undefined,
      };
      const r = await api.ai.suggestPrice(payload);
      setPriceResult(r);
    } catch (e: any) {
      setPriceResult({ raw: "Lỗi: " + (e.message || "unknown") });
    }
    setPricing(false);
  };

  const providers = cfg?.providers || {};
  const providerNames = Object.keys(providers);
  const activeMeta = activeProvider ? providerMeta(activeProvider) : null;

  return (
    <div className="page-content">
      {/* ── Section: Active provider ─────────────────────── */}
      <section className="glass-panel card-padded mb-md">
        <div className="section-header">
          <span className="material-symbols-outlined">auto_awesome</span>
          <h3>AI Provider đang dùng</h3>
        </div>
        <p style={{ margin: "0 0 14px", fontSize: 12, color: "var(--text-tertiary)", lineHeight: 1.5 }}>
          Model này dùng cho cả chat và ảnh/hóa đơn.
        </p>

        {loading ? (
          <div className="loading">Đang tải...</div>
        ) : providerNames.length === 0 ? (
          <div className="empty">
            <span className="material-symbols-outlined" style={{ fontSize: 32, color: "var(--text-tertiary)", marginBottom: 8 }}>add_circle_outline</span>
            <div>Chưa có provider nào. Thêm bên dưới để bắt đầu.</div>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "minmax(180px, 220px) 1fr auto", gap: 12, alignItems: "end" }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Provider</label>
              <select value={activeProvider} onChange={e => { setActiveProvider(e.target.value); setScannedModels([]); }}>
                <option value="">-- Chưa chọn --</option>
                {providerNames.map(n => (
                  <option key={n} value={n}>{providerMeta(n).label}</option>
                ))}
              </select>
            </div>

            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>
                Model
                {scannedModels.length > 0 && (
                  <span style={{ marginLeft: 8, fontSize: 11, color: "var(--accent-green)", fontWeight: 500 }}>
                    {scannedModels.length} model có sẵn
                  </span>
                )}
              </label>
              <div style={{ display: "flex", gap: 6 }}>
                {scannedModels.length > 0 ? (
                  <select value={activeModel} onChange={e => setActiveModel(e.target.value)} style={{ flex: 1 }}>
                    <option value="">-- Chọn model --</option>
                    {scannedModels.map(m => (
                      <option key={m.id} value={m.id}>{formatModelOptionLabel(m)}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    value={activeModel}
                    onChange={e => setActiveModel(e.target.value)}
                    placeholder="vd: meta/llama-3.1-70b-instruct"
                    style={{ flex: 1 }}
                  />
                )}
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={scanModelsForActive}
                  disabled={scanning || !activeProvider}
                  title="Quét danh sách model từ provider"
                  style={{ minWidth: 80 }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{scanning ? "hourglass_empty" : "radar"}</span>
                  {scanning ? "Quét..." : "Quét"}
                </button>
              </div>
            </div>

            <button className="btn" onClick={saveActive} disabled={saving}>
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>save</span>
              {saving ? "Đang lưu..." : "Lưu"}
            </button>
          </div>
        )}

        {activeMeta && cfg?.updated_at && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border-subtle)", display: "flex", gap: 16, fontSize: 12, color: "var(--text-tertiary)" }}>
            <span><span className="material-symbols-outlined" style={{ fontSize: 13, verticalAlign: "middle", marginRight: 4 }}>schedule</span>Cập nhật: {new Date(cfg.updated_at).toLocaleString("vi-VN")}</span>
          </div>
        )}
      </section>

      {/* ── Section: Providers list ──────────────────────── */}
      <section className="glass-panel card-padded mb-md">
        <div className="row-between mb-md">
          <div className="section-header" style={{ marginBottom: 0 }}>
            <span className="material-symbols-outlined">hub</span>
            <h3>Danh sách providers</h3>
            <span className="text-xs text-muted">
              ({providerNames.length}/{PROVIDER_OPTIONS.length})
            </span>
          </div>
          <button className="btn" onClick={openAdd} disabled={providerNames.length >= PROVIDER_OPTIONS.length}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>add</span>
            Thêm provider
          </button>
        </div>

        {loading ? (
          <div className="loading">Đang tải...</div>
        ) : providerNames.length === 0 ? (
          <div className="empty">Chưa có provider nào.</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
            {providerNames.map(name => {
              const p = providers[name];
              const meta = providerMeta(name);
              const isActive = cfg?.active === name;
              const keyCount = countProviderKeys(p);
              return (
                <div
                  key={name}
                  style={{
                    position: "relative",
                    padding: 14,
                    background: isActive ? "var(--accent-blue-bg)" : "var(--bg-card)",
                    border: `1px solid ${isActive ? "var(--accent-blue)" : "var(--border-subtle)"}`,
                    borderRadius: 12,
                    transition: "all 150ms ease",
                  }}
                >
                  {isActive && (
                    <div style={{
                      position: "absolute", top: 10, right: 10,
                      display: "inline-flex", alignItems: "center", gap: 4,
                      padding: "3px 8px", borderRadius: 999,
                      background: "var(--accent-blue)", color: "#000",
                      fontSize: 10, fontWeight: 700, letterSpacing: 0.3,
                    }}>
                      <span className="material-symbols-outlined" style={{ fontSize: 12 }}>check_circle</span>
                      ĐANG DÙNG
                    </div>
                  )}

                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 10,
                      background: isActive ? "var(--accent-blue-muted)" : "var(--bg-elevated)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0,
                    }}>
                      <span className="material-symbols-outlined" style={{ color: isActive ? "var(--accent-blue)" : "var(--text-secondary)", fontSize: 20 }}>
                        {meta.icon}
                      </span>
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {meta.label}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>{name}</div>
                    </div>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12 }}>
                      <span style={{ color: "var(--text-tertiary)" }}>API Key</span>
                      <span style={{
                        display: "inline-flex", alignItems: "center", gap: 4,
                        color: isKeylessProvider(name) ? "var(--text-secondary)" : keyCount > 0 ? "var(--accent-green)" : "var(--accent-red)",
                        fontWeight: 500,
                      }}>
                        <span className="material-symbols-outlined" style={{ fontSize: 12 }}>{isKeylessProvider(name) ? "info" : keyCount > 0 ? "check_circle" : "error"}</span>
                        {isKeylessProvider(name) ? "Không cần key" : keyCount > 0 ? `${keyCount} key` : "Chưa thiết lập"}
                      </span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, gap: 8 }}>
                      <span style={{ color: "var(--text-tertiary)", flexShrink: 0 }}>URL</span>
                      <span style={{
                        color: "var(--text-secondary)",
                        fontFamily: "ui-monospace, monospace",
                        fontSize: 11,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        direction: "rtl", textAlign: "left",
                      }}>
                        {p.base_url || <span style={{ fontStyle: "italic", color: "var(--text-tertiary)" }}>mặc định</span>}
                      </span>
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 6 }}>
                    {!isActive && (
                      <button
                        className="btn"
                        onClick={async () => {
                          setActiveProvider(name);
                          setActiveModel("");
                          setScannedModels([]);
                          try {
                            await api.ai.setActive(name, "");
                            await load();
                          } catch (e: any) {
                            alert("Lỗi: " + (e.message || "unknown"));
                          }
                        }}
                        style={{ flex: 1, minWidth: 0, height: 32, padding: "0 10px", fontSize: 12 }}
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>check_circle</span>
                        Chọn dùng
                      </button>
                    )}
                    <button
                      className="btn btn-ghost"
                      onClick={() => openEdit(name)}
                      style={{ flex: isActive ? 1 : 0, minWidth: isActive ? 0 : 70, height: 32, padding: "0 10px", fontSize: 12 }}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: 14 }}>edit</span>
                      Sửa
                    </button>
                    <button
                      className="btn btn-ghost"
                      onClick={() => removeProvider(name)}
                      title="Xoá provider"
                      style={{ minWidth: 32, width: 32, height: 32, padding: 0, color: "var(--accent-red)" }}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: 16 }}>delete_outline</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ── Section: Test translate ──────────────────────── */}
      <section className="glass-panel card-padded mb-md">
        <div className="section-header" style={{ marginBottom: 12 }}>
          <span className="material-symbols-outlined" style={{ color: "var(--accent-purple)" }}>translate</span>
          <h3>Test: Dịch ZH → VI</h3>
        </div>
        <div className="row" style={{ alignItems: "stretch", gap: 8 }}>
          <textarea
            value={translateInput}
            onChange={e => setTranslateInput(e.target.value)}
            placeholder="Nhập text tiếng Trung cần dịch..."
            rows={3}
            style={{ flex: 1, minHeight: 72 }}
          />
          <button className="btn" onClick={doTranslate} disabled={translating || !translateInput.trim()} style={{ alignSelf: "stretch", height: "auto" }}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{translating ? "hourglass_empty" : "send"}</span>
            {translating ? "Đang dịch..." : "Dịch"}
          </button>
        </div>
        {translateResult && (
          <div style={{ marginTop: 12, padding: 12, background: "var(--bg-card)", borderRadius: 8, border: "1px solid var(--border-subtle)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, fontSize: 12, color: "var(--text-tertiary)" }}>
              <span className="material-symbols-outlined" style={{ fontSize: 14 }}>translate</span>
              Kết quả
            </div>
            <p style={{ margin: 0, whiteSpace: "pre-wrap", fontSize: 14 }}>{translateResult}</p>
          </div>
        )}
      </section>

      {/* ── Section: Test pricing ────────────────────────── */}
      <section className="glass-panel card-padded mb-md">
        <div className="section-header" style={{ marginBottom: 12 }}>
          <span className="material-symbols-outlined" style={{ color: "var(--accent-yellow)" }}>price_change</span>
          <h3>Test: Gợi ý giá bán</h3>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 8, alignItems: "end" }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Tên SP</label>
            <input value={priceForm.item_name} onChange={e => setPriceForm(p => ({ ...p, item_name: e.target.value }))} placeholder="VD: Áo thun nam" />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Giá vốn (VND)</label>
            <input value={priceForm.cost} onChange={e => setPriceForm(p => ({ ...p, cost: e.target.value }))} placeholder="50000" type="number" />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Danh mục</label>
            <input value={priceForm.category} onChange={e => setPriceForm(p => ({ ...p, category: e.target.value }))} placeholder="Thời trang" />
          </div>
          <button className="btn" onClick={doSuggestPrice} disabled={pricing || !priceForm.item_name}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{pricing ? "hourglass_empty" : "lightbulb"}</span>
            {pricing ? "..." : "Gợi ý"}
          </button>
        </div>
        {priceResult && (
          <div style={{ marginTop: 12, padding: 14, background: "var(--bg-card)", borderRadius: 8, border: "1px solid var(--border-subtle)" }}>
            {priceResult.suggested_price ? (
              <>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                  <span style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Đề xuất:</span>
                  <span style={{ fontSize: 22, fontWeight: 700, color: "var(--accent-green)" }}>
                    {Number(priceResult.suggested_price).toLocaleString("vi-VN")}đ
                  </span>
                </div>
                {priceResult.reason && (
                  <p style={{ margin: "8px 0 0", color: "var(--text-secondary)", fontSize: 13, lineHeight: 1.5 }}>{priceResult.reason}</p>
                )}
              </>
            ) : (
              <p style={{ margin: 0, whiteSpace: "pre-wrap", fontSize: 13, color: "var(--text-secondary)" }}>{priceResult.raw || JSON.stringify(priceResult)}</p>
            )}
          </div>
        )}
      </section>

      {/* ── Modal: add/edit provider ─────────────────────── */}
      {showModal && (
        <div
          onClick={() => setShowModal(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 1000,
            background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 16,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              maxWidth: 520, width: "100%",
              background: "var(--bg-surface)",
              border: "1px solid var(--border-base)",
              borderRadius: 16,
              padding: 24,
              boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
              <div style={{
                width: 40, height: 40, borderRadius: 10,
                background: "var(--accent-blue-muted)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <span className="material-symbols-outlined" style={{ color: "var(--accent)", fontSize: 22 }}>
                  {modalMode === "add" ? "add_circle" : providerMeta(modalProvider).icon}
                </span>
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: 16 }}>
                  {modalMode === "add" ? "Thêm provider" : `Sửa ${providerMeta(modalProvider).label}`}
                </h3>
                <div style={{ fontSize: 12, color: "var(--text-tertiary)" }}>
                  {modalMode === "add" ? "Cấu hình AI provider mới" : isKeylessProvider(modalProvider) ? "Cập nhật Base URL" : "Cập nhật API key hoặc Base URL"}
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>Provider</label>
              <select
                value={modalProvider}
                onChange={e => onProviderChange(e.target.value)}
                disabled={modalMode === "edit"}
              >
                {PROVIDER_OPTIONS.map(p => (
                  <option key={p.value} value={p.value} disabled={modalMode === "add" && !!cfg?.providers[p.value]}>
                    {p.label}{modalMode === "add" && cfg?.providers[p.value] ? " (đã có)" : ""}
                  </option>
                ))}
              </select>
            </div>

            {!isKeylessProvider(modalProvider) && (<>
            <div className="form-group">
              <label>
                API Key chính
                {modalMode === "edit" && <span style={{ marginLeft: 6, fontSize: 11, color: "var(--text-tertiary)", fontWeight: 400 }}>(để trống = giữ nguyên)</span>}
              </label>
              <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                <input
                  type={showKey ? "text" : "password"}
                  value={modalApiKey}
                  onChange={e => setModalApiKey(e.target.value)}
                  placeholder={modalMode === "edit" ? "••••••••" : "Nhập API key chính"}
                  name="ai-provider-secret-token"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  data-lpignore="true"
                  data-1p-ignore="true"
                  data-form-type="other"
                  readOnly
                  onFocus={e => e.currentTarget.removeAttribute("readonly")}
                  style={{ flex: 1, paddingRight: 38 }}
                />
                <button
                  type="button"
                  onClick={toggleShowKey}
                  disabled={revealing}
                  title={showKey ? "Ẩn key" : "Xem key đã lưu"}
                  style={{ position: "absolute", right: 6, background: "none", border: "none", cursor: revealing ? "wait" : "pointer", color: "var(--text-tertiary)", display: "flex", alignItems: "center", padding: 4 }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
                    {revealing ? "hourglass_empty" : showKey ? "visibility_off" : "visibility"}
                  </span>
                </button>
              </div>
              <p style={{ margin: "8px 0 0", fontSize: 11, color: "var(--text-tertiary)", lineHeight: 1.45 }}>
                Nhiều key sẽ tự xoay vòng khi bị giới hạn (429).
                {modalMode === "edit" && cfg?.providers[modalProvider] && (
                  <span style={{ marginLeft: 6, color: "var(--text-secondary)" }}>
                    Đã lưu: {countProviderKeys(cfg.providers[modalProvider])} key
                  </span>
                )}
              </p>
            </div>

            <div className="form-group">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <label style={{ marginBottom: 0 }}>Key phụ (xoay vòng)</label>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => setModalExtraKeys(keys => [...keys, ""])}
                  style={{ height: 28, padding: "0 10px", fontSize: 12 }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 14 }}>add</span>
                  Thêm key
                </button>
              </div>
              {modalExtraKeys.length === 0 ? (
                <div style={{ fontSize: 12, color: "var(--text-tertiary)", fontStyle: "italic", padding: "8px 0" }}>
                  Chưa có key phụ. Bấm &quot;Thêm key&quot; để thêm key dự phòng.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {modalExtraKeys.map((keyVal, idx) => (
                    <div key={idx} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <span style={{ fontSize: 11, color: "var(--text-tertiary)", minWidth: 28 }}>#{idx + 2}</span>
                      <input
                        type={showKey ? "text" : "password"}
                        value={keyVal}
                        onChange={e => setModalExtraKeys(keys => keys.map((k, i) => (i === idx ? e.target.value : k)))}
                        placeholder="API key phụ"
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck={false}
                        data-lpignore="true"
                        data-1p-ignore="true"
                        data-form-type="other"
                        readOnly
                        onFocus={e => e.currentTarget.removeAttribute("readonly")}
                        style={{ flex: 1 }}
                      />
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => setModalExtraKeys(keys => keys.filter((_, i) => i !== idx))}
                        title="Xoá key này"
                        style={{ minWidth: 32, width: 32, height: 32, padding: 0, color: "var(--accent-red)" }}
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: 16 }}>close</span>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            </>)}

            <div className="form-group">
              <label>
                Base URL
                <span style={{ marginLeft: 6, fontSize: 11, color: "var(--text-tertiary)", fontWeight: 400 }}>(optional)</span>
              </label>
              <input
                value={modalBaseUrl}
                onChange={e => setModalBaseUrl(e.target.value)}
                placeholder={providerMeta(modalProvider).defaultUrl || "Default"}
              />
            </div>

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 20, paddingTop: 16, borderTop: "1px solid var(--border-subtle)" }}>
              <button className="btn btn-ghost" onClick={() => setShowModal(false)} disabled={modalSaving}>Huỷ</button>
              <button className="btn" onClick={saveProvider} disabled={modalSaving}>
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>save</span>
                {modalSaving ? "Đang lưu..." : "Lưu"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
